import React, { useState, useEffect, useContext } from "react";
import styles from "../scss/AppForm.module.scss";
import cx from "classnames";
import { useHistory, useParams } from "react-router-dom";
import AFLayout from "../components/AFLayout";
import { IAnswerProps, IAppFormQuestion, IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import useAFApplication from "../hooks/useAFApplication";
import { getEligibilityQuestions, setCurrentPageProgress, handleEligibilityAnswers } from "../api/ApiAppForm";
import AppQuestionForm from "../components/AppQuestionForm";
import AFMultiForms from "../components/AFMultiForms";
import {
    getQuestionLabel,
    getQuestionType
} from "../shared/AppFormHelper";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { PageType } from "../enums/PageType";
import AFLoadingSpinner from "../components/AFLoadingSpinner";
import AFSecure from "../components/AFSecure";
import AFCannotContinue from "../components/AFCannotContinue";

type AFParams = {
    applicant: string;
};

const AFEligibilityPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<AFParams>();
    const applicantParams = params.applicant;
    const [isFormReady, setIsFormReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState<IAppFormQuestion>();
    const [currentQuestionList, setCurrentQuestionList] = useState<IAppFormQuestion[]>();
    const [isMultipleQuestion, setIsMultipleQuestion] = useState(false);
    const [noAvailableQuestion, setNoAvailableQuestion] = useState(false);
    const [formQuestionLabel, setFormQuestionLabel] = useState("");
    const [formQuestionHelpText, setFormQuestionHelpText] = useState("");
    const [formQuestionType, setFormQuestionType] = useState("");
    const [isInErrorState, setIsInErrorState] = useState(false);
    const [isIneligible, setIsIneligible] = useState(false);
    const [reason, setReason] = useState("");

    const { actions, selectors } = useContext(AppFormControllerContext);
    const { createApp } = useAFApplication();

    const getQuestions = (): void => {
        const applicantList = selectors.getApplicantList();
        const currentApplicant = applicantList.find((i) => i.params === applicantParams);
        getEligibilityQuestions(selectors.getApplicationId(), selectors.getQuoteGroupId(), currentApplicant.clientNumber)
            .then((res) => {
                setIsFormReady(true);
                if (!res?.length) {
                    setIsInErrorState(true);
                    return;
                }
                setCurrentQuestionList([]);
                setFormQuestionLabel("");
                setIsMultipleQuestion(res?.length > 1);
                setNoAvailableQuestion(false);
                setIsFormReady(true);
                if (res?.length > 1) {
                    const firstQuestion = res[0];
                    setCurrentQuestionList(res);
                    setFormQuestionLabel(getQuestionLabel(firstQuestion));
                    setFormQuestionHelpText(firstQuestion?.questionHeaderHelp)
                    setFormQuestionType(getQuestionType(firstQuestion));
                    return;
                } else if (res?.length) {
                    setCurrentQuestion(res[0]);
                    return;
                }
                setNoAvailableQuestion(true);
            });
    };

    const handleSubmit = (answers: IAnswerProps[]): void => {
        setIsFormReady(false);
        const applicationId = selectors.getApplicationId();
        const groupId = selectors.getQuoteGroupId();
        const applicantList = selectors.getApplicantList();
        const currentApplicant = applicantList.find((i) => i.params === applicantParams);
        setIsSubmitting(true);
        handleEligibilityAnswers(applicationId, groupId, currentApplicant.clientNumber, answers)
            .then((res) => {
                if (res?.isEligible) {
                    const nextApplicant = applicantList.find((i) => i.clientNumber === currentApplicant?.clientNumber + 1);
                    setIsFormReady(false);
                    if (nextApplicant) {
                        history.push(`/app-form/eligibility/${nextApplicant?.params}`);
                        return;
                    }
                    createApp(
                        groupId,
                        applicationId,
                        selectors.getInsurerId(),
                        applicantList
                    ).then((res) => {
                        const applicants = res?.applicants;
                        if (!applicants || !applicants?.length) return;
                        actions.setApplicationId(res.applicationId);
                        actions.setApplicationToken(res.applicationToken);
                        actions.setApplicantList(res?.applicants);
                        const pageInfo: IPageInfo = {
                            pageType: PageType.AppForm,
                            currentClientNumber: 1
                        }
                        setCurrentPageProgress(res?.applicationId, selectors.getQuoteGroupId(), pageInfo);
                        history.push(`/app-form/details/${applicants[0].applicantToken}`);
                        setIsSubmitting(false);
                    }).catch((error) => {
                        setIsInErrorState(true);
                    });
                } else {
                    setIsIneligible(true);
                    setReason(res?.ineligibleReason);
                }
            })
            .catch(() => {
                useErrorLogger();
                setIsInErrorState(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const getApplicantName = (): string => {
        const applicant = selectors.getEachApplicantInfo(params.applicant);
        return applicant.firstName;
    };

    const handleBack = (e): void => {
        e.preventDefault();
        if (isSubmitting) return;
        const applicantList = selectors.getApplicantList();
        const currentApplicant = applicantList.find((i) => i.params === applicantParams);
        const previousApplicant = applicantList.find((i) => i.clientNumber === currentApplicant?.clientNumber - 1);
        if (!previousApplicant) { //this is the first applicant
            const lastApplicant = applicantList.reduce((prev, current) => (prev && prev.clientNumber > current.clientNumber) ? prev : current);
            if (lastApplicant.isChild) {
                history.push(`/app-form/setup/dateofbirth/applicant${lastApplicant.clientNumber}`);
            } else {
                history.push(`/app-form/setup/contact/applicant${lastApplicant.clientNumber}`);
            }
        } else {
            history.push(`/app-form/eligibility/applicant${previousApplicant.clientNumber}`);
        }
    };

    useEffect(() => {
        getQuestions();
    }, []);

    return (
        <AFLayout isInErrorState={isInErrorState} currentPage={PageType.Setup}>
            <div className={cx(styles["advice-progress"], "layout-flex-column", styles["layout"])}>
                <div className={styles["back-btn-wrapper"]}>
                    <AFSecure />
                </div>
                {isSubmitting ? (
                    <AFLoadingSpinner />
                ) : (
                    isIneligible ? (
                        <AFCannotContinue
                            reason={[reason]}
                        />
                    ) : (
                        <>
                            {isFormReady &&
                                !isMultipleQuestion &&
                                !noAvailableQuestion && (
                                    <AppQuestionForm
                                        applicantName={getApplicantName()}
                                        question={currentQuestion}
                                        nextQuestion={handleSubmit}
                                        handleBack={handleBack}
                                        skipIsVisible={false}
                                    />
                                )}

                            {isFormReady &&
                                isMultipleQuestion &&
                                currentQuestionList?.length &&
                                !noAvailableQuestion && (
                                    <AFMultiForms
                                        applicantName={getApplicantName()}
                                        list={currentQuestionList}
                                        nextQuestion={handleSubmit}
                                        formLabel={formQuestionLabel}
                                        helpText={formQuestionHelpText}
                                        formQuestionType={formQuestionType}
                                        handleBack={handleBack}
                                        skipIsVisible={false}
                                    />
                                )}
                        </>
                    ))}
            </div>
        </AFLayout>
    );
};

export default AFEligibilityPage;